import { audio } from '../audio/audio';
import { criticalImpact, solarGrazingArcsec } from '../core/analytic';
import { MASS_SPECS } from '../core/constants';
import { store } from '../game/store';
import { trayIcon } from '../render/sprites';
import { ICONS, h, icon } from './dom';

/** Open a modal dialog; returns a close function. Focus returns to the opener on close. */
export function openDialog(
  titleText: string,
  body: HTMLElement,
  foot: HTMLElement | null = null,
  opts: { onClose?: () => void; labelId?: string } = {},
): () => void {
  const opener = document.activeElement as HTMLElement | null;
  const id = opts.labelId ?? `dlg-${Math.random().toString(36).slice(2, 8)}`;
  const close = () => {
    if (dlg.open) dlg.close();
  };
  const dlg = h(
    'dialog',
    { 'aria-labelledby': id },
    h(
      'div',
      { class: 'dlg' },
      h(
        'div',
        { class: 'dlg-head' },
        h('h2', { id, text: titleText }),
        h(
          'button',
          { class: 'icon-btn', type: 'button', 'aria-label': 'Close', onclick: close },
          icon(ICONS.close),
        ),
      ),
      body,
      foot ?? '',
    ),
  );
  dlg.addEventListener('close', () => {
    dlg.remove();
    opts.onClose?.();
    opener?.focus?.();
  });
  // Clicking the backdrop closes the dialog.
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg) close();
  });
  document.body.append(dlg);
  dlg.showModal();
  return close;
}

/** Ask a yes/no question. Resolves true when confirmed. */
export function confirmDialog(
  titleText: string,
  message: string,
  yes: string,
  no = 'Cancel',
): Promise<boolean> {
  return new Promise((resolve) => {
    let answer = false;
    const body = h('div', { class: 'dlg-body' }, h('p', { text: message }));
    const yesBtn = h('button', { class: 'btn primary', type: 'button', text: yes });
    const noBtn = h('button', { class: 'btn', type: 'button', text: no });
    const close = openDialog(titleText, body, h('div', { class: 'dlg-foot' }, noBtn, yesBtn), {
      onClose: () => resolve(answer),
    });
    yesBtn.addEventListener('click', () => {
      answer = true;
      close();
    });
    noBtn.addEventListener('click', close);
    yesBtn.focus();
  });
}

function switchRow(label: string, hint: string, value: boolean, onChange: (v: boolean) => void) {
  const id = `sw-${label.replace(/\W+/g, '-').toLowerCase()}`;
  const sw = h('button', {
    class: 'switch',
    type: 'button',
    role: 'switch',
    'aria-checked': String(value),
    'aria-labelledby': id,
  });
  sw.addEventListener('click', () => {
    const v = sw.getAttribute('aria-checked') !== 'true';
    sw.setAttribute('aria-checked', String(v));
    onChange(v);
  });
  return h(
    'div',
    { class: 'setting' },
    h('div', { class: 'desc' }, h('span', { id, text: label }), h('small', { text: hint })),
    sw,
  );
}

export function openSettings(): void {
  const s = store.settings;
  const vol = h('input', {
    type: 'range',
    min: '0',
    max: '100',
    step: '5',
    value: String(Math.round(s.volume * 100)),
    'aria-label': 'Volume',
  }) as HTMLInputElement;
  vol.addEventListener('input', () => {
    store.updateSettings({ volume: Number(vol.value) / 100 });
  });
  vol.addEventListener('change', () => audio.chime(2));

  const motion = h('div', { class: 'seg', role: 'radiogroup', 'aria-label': 'Motion' });
  const opts: [typeof s.motion, string][] = [
    ['system', 'System'],
    ['reduce', 'Reduced'],
    ['full', 'Full'],
  ];
  for (const [val, text] of opts) {
    const b = h('button', {
      type: 'button',
      role: 'radio',
      'aria-checked': String(s.motion === val),
      text,
    });
    b.addEventListener('click', () => {
      store.updateSettings({ motion: val });
      motion
        .querySelectorAll('button')
        .forEach((x) => x.setAttribute('aria-checked', String(x === b)));
    });
    motion.append(b);
  }

  const reset = h('button', { class: 'btn danger', type: 'button', text: 'Reset progress' });
  reset.addEventListener('click', async () => {
    const ok = await confirmDialog(
      'Reset progress?',
      'This clears every star and endless record on this device. It cannot be undone.',
      'Reset',
    );
    if (ok) store.resetProgress();
  });

  const body = h(
    'div',
    { class: 'dlg-body' },
    switchRow('Sound', 'Synthesised pad, chimes and effects.', s.sound, (v) => {
      store.updateSettings({ sound: v });
      if (v) {
        audio.unlock();
        audio.chime(0);
      }
    }),
    h(
      'div',
      { class: 'setting' },
      h(
        'div',
        { class: 'desc' },
        h('span', { text: 'Volume' }),
        h('small', { text: 'Master level.' }),
      ),
      vol,
    ),
    h(
      'div',
      { class: 'setting' },
      h(
        'div',
        { class: 'desc' },
        h('span', { text: 'Motion' }),
        h('small', { text: 'Reduced removes shimmer and particles; feedback stays.' }),
      ),
      motion,
    ),
    switchRow(
      'Colour-blind patterns',
      'Beams get dash patterns; colours get symbols everywhere.',
      s.patterns,
      (v) => store.updateSettings({ patterns: v }),
    ),
    switchRow('High contrast', 'Plain dark plate, brighter and thicker lines.', s.contrast, (v) =>
      store.updateSettings({ contrast: v }),
    ),
    switchRow('Vibration', 'Gentle haptics on phones that support it.', s.haptics, (v) =>
      store.updateSettings({ haptics: v }),
    ),
    h(
      'div',
      { class: 'setting' },
      h(
        'div',
        { class: 'desc' },
        h('span', { text: 'Progress' }),
        h('small', { text: 'Stars are saved in this browser only.' }),
      ),
      reset,
    ),
  );
  openDialog('Settings', body);
}

// ------------------------------------------------------------------------------------------- help

const math = (s: string) => {
  const span = h('span', { class: 'eq' });
  span.innerHTML = s;
  return span;
};

function legendRow(kind: keyof typeof MASS_SPECS, text: string) {
  const c = trayIcon(kind, 40, Math.min(2, window.devicePixelRatio || 1));
  c.setAttribute('aria-hidden', 'true');
  return [c, h('div', { html: text })];
}

function howToPlay(): HTMLElement {
  return h(
    'div',
    {},
    h('h3', { text: 'The goal' }),
    h('p', {
      text: 'Beacons shine beams of light in straight lines. Light every golden telescope at the same time to complete the plate.',
    }),
    h('h3', { text: 'Your only tool is gravity' }),
    h('p', {
      text: 'Drag masses from the tray onto the plate. Every beam that passes a mass bends towards it: closer bends more, heavier bends more. One mass bends every beam near it, so each placement is a small trade-off.',
    }),
    h(
      'div',
      { class: 'legend' },
      ...legendRow('moon', '<strong>Moon</strong> (weight 1): a gentle pull.'),
      ...legendRow('star', '<strong>Star</strong> (weight 2): bends harder, from farther away.'),
      ...legendRow(
        'giant',
        '<strong>Giant</strong> (weight 3): strong enough to turn a beam right round.',
      ),
      ...legendRow(
        'hole',
        '<strong>Black hole</strong>: a beam aimed inside the dashed ring falls in and is lost. Just outside it, light can loop.',
      ),
      ...legendRow(
        'void',
        '<strong>Void</strong> (negative mass, weight 2): pushes light away instead of pulling.',
      ),
    ),
    h('h3', { text: 'Telescopes, colour and focus' }),
    h(
      'ul',
      {},
      h('li', {
        text: 'Each telescope needs an exact colour of light, shown by its inner ring and symbols (triangle = red, diamond = green, square = blue).',
      }),
      h('li', {
        text: 'Light mixes: red and green arriving together make yellow; red, green and blue make white. A wrong colour spoils the telescope.',
      }),
      h('li', {
        text: 'Dots around a telescope mean it needs two or three beams at once: focus a fan of beams with gravity, like a lens.',
      }),
      h('li', {
        text: 'Gravity bends every colour equally, so colour comes from beacons and from nebulae, which only let their own colour through.',
      }),
      h('li', {
        text: 'Walls, rocks and masses block light. Dust swallows it. Engraved masses are fixed.',
      }),
    ),
    h('h3', { text: 'Stars' }),
    h('p', {
      text: 'Each plate has a par: the total weight of the masses in our reference solution. Win at or under par for three stars, within two of par for two. A hint reveals where one reference mass goes and costs a star.',
    }),
    h('h3', { text: 'Controls' }),
    h(
      'table',
      {},
      h(
        'tr',
        {},
        h('th', { text: 'Mouse / touch' }),
        h('td', {
          text: 'Drag from the tray to place, drag to move, drag back to the tray (or off the plate) to remove. Tap a tray mass to drop it in a free spot. On touch screens the mass rides just above your finger so you can see the beams.',
        }),
      ),
      h(
        'tr',
        {},
        h('th', { text: 'Keyboard' }),
        h('td', {
          text: 'Tab to a tray mass and press Enter to place it. Arrow keys move the selected mass (Shift: big steps, Alt: fine steps). Delete returns it to the tray. Ctrl+Z undoes.',
        }),
      ),
      h(
        'tr',
        {},
        h('th', { text: 'Rings' }),
        h('td', {
          text: 'While you drag, dotted rings show where a passing beam would bend by about 10° and 4°.',
        }),
      ),
    ),
  );
}

function aboutPhysics(): HTMLElement {
  const sun = solarGrazingArcsec().toFixed(2);
  const bc = criticalImpact(MASS_SPECS.hole.mu).toFixed(0);
  return h(
    'div',
    {},
    h('h3', { text: 'Real light bending' }),
    h('p', {
      text: 'In general relativity, a ray of light passing a mass M at distance b is deflected by',
    }),
    math(
      '<math display="block"><mi>α</mi><mo>=</mo><mfrac><mrow><mn>4</mn><mi>G</mi><mi>M</mi></mrow><mrow><msup><mi>c</mi><mn>2</mn></msup><mi>b</mi></mrow></mfrac></math>',
    ),
    h('p', {
      text: `For starlight grazing the Sun that is ${sun} arcseconds: the effect first measured during the 1919 solar eclipse. Galaxies and clusters bend the light of more distant galaxies into arcs and, when perfectly aligned, Einstein rings.`,
    }),
    h('h3', { text: 'The game’s light' }),
    h('p', {
      text: 'Each beam follows the ray equation of geometric optics through an effective refractive index set by the masses:',
    }),
    math(
      '<math display="block"><mi>n</mi><mo>=</mo><mi>exp</mi><mo>(</mo><munder><mo>∑</mo><mi>i</mi></munder><mfrac><msub><mi>μ</mi><mi>i</mi></msub><msub><mi>r</mi><mi>i</mi></msub></mfrac><mo>)</mo></math>',
    ),
    h('p', { text: 'so the beam’s unit direction t̂ turns, per unit length s, by' }),
    math(
      '<math display="block"><mfrac><mrow><mi>d</mi><mover><mi>t</mi><mo>^</mo></mover></mrow><mrow><mi>d</mi><mi>s</mi></mrow></mfrac><mo>=</mo><mi>g</mi><mo>−</mo><mo>(</mo><mover><mi>t</mi><mo>^</mo></mover><mo>·</mo><mi>g</mi><mo>)</mo><mover><mi>t</mi><mo>^</mo></mover><mo>,</mo><mspace width="0.6em"/><mi>g</mi><mo>=</mo><mi>∇</mi><mo>ln</mo><mi>n</mi></math>',
    ),
    h('p', {
      text: 'That is a pull towards every mass falling off as M/r², applied sideways so light keeps its speed. Far from a mass it gives exactly the weak-field form α = 2μ/b, with μ = 2GM: the same 1/b law as Einstein’s formula, with G turned up by many orders of magnitude so a moon bends a beam by degrees instead of microarcseconds.',
    }),
    h('h3', { text: 'Black holes and voids' }),
    h('p', {
      text: `This medium has a photon sphere at r = μ: rays arriving with impact parameter below e·μ (about ${bc} units for the game’s black hole) spiral in and are captured, while rays just outside wind round, sometimes more than a full turn. Real Schwarzschild holes behave alike, with the photon sphere at 3GM/c² and the critical impact parameter at 3√3 GM/c². Voids are negative masses (μ < 0): no such thing is known to exist, but the same equation makes them push light away.`,
    }),
    h('h3', { text: 'What is simplified' }),
    h(
      'ul',
      {},
      h('li', {
        text: 'Everything happens in a flat 2D plane; masses never move and do not feel each other.',
      }),
      h('li', {
        text: 'No time delay, no redshift, no brightness or magnification: a beam is a single ray.',
      }),
      h('li', {
        text: 'Strength is tuned for play, and bodies have a finite radius that blocks light.',
      }),
    ),
    h('h3', { text: 'How it is computed and checked' }),
    h('p', {
      text: 'Rays are integrated with fourth-order Runge–Kutta in arc length, with steps refined near masses (at most 0.025 rad of turning per step), and collisions are tested exactly along each step. Automated tests compare the integrator with the analytic small-angle law (within 1%), with the exact deflection integral of this medium, with the conserved quantity n·r·sinψ, and with time reversal. It is an educational, game-tuned simulation, not a research tool.',
    }),
  );
}

function aboutGame(): HTMLElement {
  return h(
    'div',
    {},
    h('h3', { text: 'Gravity is the only tool' }),
    h('p', {
      text: 'Light bent by gravity has appeared as one element among many in other games (God of Light, Gravia, Enigmo and Lightbender all include it alongside mirrors, prisms or other tools), and beam-routing puzzles such as Deflektor, Chromatron, Lazors and Laser Maze use mirrors. Lensing keeps to one idea: there are no mirrors or prisms, only masses. Because gravity bends every colour equally, colour puzzles come from beacons, telescopes and nebulae; several masses together make lenses, focus fans of beams and invert pictures.',
    }),
    h('h3', { text: 'Levels' }),
    h('p', {
      text: 'All 30 plates were designed by construction and checked by script: each has a stored reference solution that the automated tests replay, the empty board never wins, and a solver searched for anything lighter than par. Endless mode builds puzzles the same way: it hides masses, traces the beams, puts telescopes where the light lands, adds rocks only on the unbent paths, and then hands you the masses.',
    }),
    h('h3', { text: 'Credits' }),
    h(
      'ul',
      {},
      h('li', {
        html: 'Design and code: <a href="https://mustafaobaidd1.github.io/">Mustafa Obaid</a>. <a href="https://github.com/mustafaobaidd1/lensing-game">Source code</a> (MIT).',
      }),
      h('li', {
        html: 'Typefaces: <a href="https://github.com/duartp/gloock">Gloock</a> (© 2022 The Gloock Project Authors) and <a href="https://github.com/googlefonts/atkinson-hyperlegible-next">Atkinson Hyperlegible Next</a> (© 2020–2024 The Atkinson Hyperlegible Next Project Authors), both under the SIL Open Font License 1.1.',
      }),
      h('li', { text: 'All sound is synthesised in the browser; all art is drawn in code.' }),
    ),
  );
}

export function openHelp(tab: 'play' | 'physics' | 'game' = 'play'): void {
  const panels: Record<string, () => HTMLElement> = {
    play: howToPlay,
    physics: aboutPhysics,
    game: aboutGame,
  };
  const names: [string, string][] = [
    ['play', 'How to play'],
    ['physics', 'About the physics'],
    ['game', 'About'],
  ];
  const content = h('div', { class: 'dlg-body', role: 'tabpanel', tabindex: '0' });
  const tabs = h('div', { class: 'tabs', role: 'tablist', 'aria-label': 'Help sections' });
  const select = (key: string) => {
    content.replaceChildren(panels[key]!());
    content.scrollTop = 0;
    tabs.querySelectorAll('button').forEach((b) => {
      const on = b.dataset.key === key;
      b.setAttribute('aria-selected', String(on));
      b.tabIndex = on ? 0 : -1;
      if (on) content.setAttribute('aria-labelledby', b.id);
    });
  };
  names.forEach(([key, text]) => {
    const b = h('button', { type: 'button', role: 'tab', id: `tab-${key}`, 'data-key': key, text });
    b.addEventListener('click', () => select(key));
    b.addEventListener('keydown', (e) => {
      const i = names.findIndex(([k]) => k === key);
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const j = (i + (e.key === 'ArrowRight' ? 1 : names.length - 1)) % names.length;
        const nextKey = names[j]![0];
        select(nextKey);
        (tabs.querySelector(`[data-key="${nextKey}"]`) as HTMLButtonElement).focus();
        e.preventDefault();
      }
    });
    tabs.append(b);
  });
  const wrap = h('div', { style: { display: 'contents' } }, tabs, content);
  const body = h(
    'div',
    {
      style: {
        display: 'grid',
        gridTemplateRows: 'auto minmax(0,1fr)',
        minHeight: '0',
        overflow: 'hidden',
      },
    },
    wrap,
  );
  openDialog('Lensing', body);
  select(tab);
}
